import { DialogTitle } from "@mui/material";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useResetCustomerDependentControls } from "pages/common/hooks/use-reset-customer-dependent-controls";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";

import { quotationBusiness } from "api/bussiness/quotation/quotation-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { IUpdateQuotation } from "mobx-stores/_models/quotation/quotation";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditQuotationFormSchema } from "../schema";

export const EditQuotation = observer(() => {
  const {
    quotationManagement: {
      openEdit,
      getQuotationDetail,
      setProp,
      quotationDetail,
    },
  } = useStores();
  const [enableDependentReset, setEnableDependentReset] = useState(false);
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
  } = useForm<IUpdateQuotation>((form) => getEditQuotationFormSchema(form), {
    id: "",
    date: dateTimeToUTCString(dayjs()),
    number: "",
    customerId: "",
    receiverInfo: "",
    note: "",
    deliveryAddressId: "",
    items: [],
  });

  useEffect(() => {
    if (quotationDetail) {
      setEnableDependentReset(false);
      setDefaults({
        id: quotationDetail.id || "",
        date: quotationDetail.date || "",
        number: quotationDetail.number || "",
        customerId: quotationDetail.customerId || "",
        receiverInfo: quotationDetail.receiverInfo || "",
        note: quotationDetail.note || "",
        deliveryAddressId: quotationDetail.deliveryAddressId || "",
        items: quotationDetail.items.map((x) => ({
          id: x.id || "",
          amount: x.amount,
          note: x.note || "",
          productId: x.productId || "",
          unitPrice: x.unitPrice || 0,
          oldUnitPriceDateFirst: x.oldUnitPriceDateFirst || null,
          oldUnitPriceFirst: x.oldUnitPriceFirst || null,
          oldUnitPriceDateSecond: x.oldUnitPriceDateSecond || null,
          oldUnitPriceSecond: x.oldUnitPriceSecond || null,
        })),
      });
      if (quotationDetail.items && quotationDetail.items.length > 0) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = quotationDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", [
              {
                label: createProductOptionLabel(data.product),
                value: data.product?.id,
              },
            ]);
          }
        });
      }

      setEnableDependentReset(true);
    }
  }, [getControl, quotationDetail, setDefaults]);

  useEffect(() => {
    const customerCtr = getControl("customerId");
    customerCtr.setProp("optionItems", [
      {
        value: quotationDetail?.customer?.id,
        label: `${quotationDetail?.customer?.dealingName || ""}`,
      },
    ]);
  }, [
    getControl,
    quotationDetail?.customer?.dealingName,
    quotationDetail?.customer?.id,
  ]);

  useEffect(() => {
    const deliveryAddressCtr = getControl("deliveryAddressId");
    deliveryAddressCtr.setProp("optionItems", [
      {
        value: quotationDetail?.deliveryAddress?.id,
        label: `${quotationDetail?.deliveryAddress?.address || ""}`,
      },
    ]);
  }, [
    getControl,
    quotationDetail?.deliveryAddress?.address,
    quotationDetail?.deliveryAddress?.id,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateQuotation) => {
      try {
        if (!quotationDetail?.id) return;
        await quotationBusiness.update(quotationDetail?.id, data);
        await getQuotationDetail(quotationDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getQuotationDetail, quotationDetail?.id, setProp],
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const customerControl = useMemo(() => {
    return getControl("customerId");
  }, [getControl]);

  const deliveryAddressControl = useMemo(() => {
    return getControl("deliveryAddressId");
  }, [getControl]);

  useResetCustomerDependentControls({
    customerId: customerControl.value,
    controls: [{ control: deliveryAddressControl }],
    arrayChildControls: [
      {
        arrayControl: itemControl,
        childControlName: "productId",
      },
    ],
    enabled: enableDependentReset,
  });

  const quotationItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl],
  );

  const addNewQuotationItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa phiếu báo giá</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm controls={controls}></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewQuotationItemRow}
        >
          {quotationItems && quotationItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={quotationItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
